// LOCK-STEP MIRROR: keep in sync with InviteCloudBackend/src/modules/experiences/scene/manifest.ts
// Any change to the schema body must be applied to both copies simultaneously.
import { z } from 'zod';
import { RITUAL_TYPES, TEMPLATE_STYLES, OCCASIONS, zVenue } from './dsl';

const zPublicAssetUrl = z.string().refine(
  (url) => /^\/(?!\/)/.test(url) || z.string().url().safeParse(url).success,
  { message: 'Expected an absolute URL or root-relative public asset path' },
);

export const zAssetRef = z.object({
  publicUrl: zPublicAssetUrl,
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative().optional(),
  dimensions: z.object({ width: z.number(), height: z.number() }).optional(),
  durationSeconds: z.number().optional(),
  provider: z.string().optional(),  // 'gemini' | 'stock' | ...
});

// T14: widened particle enum (T15 adds more; these are the base set)
export const PARTICLE_EFFECTS = [
  'petals', 'yellow_dust', 'sparkles', 'embers', 'none',
  // T15 additions:
  'confetti', 'balloons', 'fireflies', 'snowfall', 'bubbles', 'rose_petals',
] as const;
export type ParticleEffect = typeof PARTICLE_EFFECTS[number];

// T14: widened entry animation enum
export const ENTRY_ANIMATIONS = [
  'fade_up', 'zoom_in', 'curtain_reveal', 'color_wash',
  // T15 additions:
  'flip_3d', 'split_reveal', 'letter_cascade', 'depth_zoom',
] as const;
export type EntryAnimation = typeof ENTRY_ANIMATIONS[number];

// T15: interactive widget kinds (additive — null/undefined = none)
export const INTERACTIVE_KINDS = [
  'tilt_card', 'sticky_story', 'countdown_flip', 'parallax_layers', 'reveal_scratch',
  'light_diya', 'lift_veil', 'petal_shower',
  'panorama', 'portals', 'living', 'corridor', 'depth',
] as const;
export type InteractiveKind = typeof INTERACTIVE_KINDS[number];

export const zDecorAsset = z.object({
  url: z.string().min(1),
  alt: z.string().optional(),
});

export const zDecorConfig = z.object({
  frame: zDecorAsset.optional(),
  deity: zDecorAsset.optional(),
  corner: zDecorAsset.optional(),
  divider: zDecorAsset.optional(),
  intensity: z.enum(['subtle', 'ornate', 'ceremonial']).default('subtle'),
}).optional();

export const zTypographyConfig = z.object({
  nameTreatment: z.enum(['calligraphy_reveal', 'editorial_serif', 'festival_script']).default('calligraphy_reveal'),
  foil: z.enum(['gold', 'champagne', 'rose', 'peacock']).default('gold'),
  monogram: z.string().max(8).optional(),
}).optional();

export const zSceneAssets = z.object({
  renderStrategy: z.enum(['video_primary', 'image_primary', 'gradient_only']),
  primaryImage: zAssetRef,          // ALWAYS present — compiler enforces this
  primaryVideo: zAssetRef.optional(),
  particleEffect: z.enum(PARTICLE_EFFECTS).default('none'),
});

export const zAnimationConfig = z.object({
  entry: z.enum(ENTRY_ANIMATIONS),
  parallax: z.object({ enabled: z.boolean(), intensity: z.number().min(0).max(1) }),
  scrollTriggerThreshold: z.number().min(0).max(1).default(0.3),
});

export const zMobileBehavior = z.object({
  disableVideo: z.boolean().default(false),
  reduceParticles: z.boolean().default(true),
});

export const zSceneSection = z.object({
  sectionId: z.string(),
  // T14: momentId generalises ritualId; ritualId kept as alias for back-compat
  momentId: z.string(),
  ritualId: z.string().optional(),  // backward-compat alias (= momentId for weddings)
  occasion: z.enum(OCCASIONS).optional(),  // additive — wedding sections may omit
  order: z.number().int(),
  content: z.object({
    title: z.string(),
    subtitle: z.string().optional(),
    date: z.string(),
    time: z.string().optional(),
    venue: zVenue.optional(),   // optional now — story/memory moments have no venue
    dressCode: z.string().optional(),
    customNote: z.string().optional(),
  }),
  assets: zSceneAssets,
  animation: zAnimationConfig,
  mobileBehavior: zMobileBehavior,
  interactive: z.object({
    kind: z.enum(INTERACTIVE_KINDS),
  }).optional(),
  decor: zDecorConfig,
});

export const zSceneManifest = z.object({
  version: z.literal('1.0'),
  invitationId: z.string(),
  generatedAt: z.string(),  // ISO 8601 datetime
  template: z.string(),     // T14: widened from enum (style packs grow in T15)
  occasion: z.enum(OCCASIONS).optional(),  // additive — existing manifests may omit
  hero: z.object({
    // T14: generic names (brideName/groomName kept as optional back-compat aliases)
    primaryName: z.string().optional(),
    secondaryName: z.string().optional(),
    brideName: z.string().optional(),   // back-compat
    groomName: z.string().optional(),   // back-compat
    eventDate: z.string().optional(),
    weddingDate: z.string().optional(), // back-compat
    tagline: z.string().optional(),
    personPhotoUrl: z.string().optional(), // Birthday person uploaded photo URL
    personPhoto: zAssetRef.optional(),
    background: zSceneAssets,
    decor: zDecorConfig,
  }),
  opening: z.object({
    enabled: z.boolean().default(true),
    title: z.string().optional(),
    subtitle: z.string().optional(),
    skipStorageKey: z.string().optional(),
  }).optional(),
  music: z.object({
    url: z.string().min(1),
    title: z.string().optional(),
  }).optional(),
  typography: zTypographyConfig,
  sections: z.array(zSceneSection),
  footer: z.object({
    venue: zVenue.optional(),   // optional — some occasions have no single footer venue
    rsvpLink: z.string().url().optional(),
    calendar: z.object({
      title: z.string(),
      startsAt: z.string(),
      endsAt: z.string().optional(),
    }),
  }),
});

export type SceneManifest = z.infer<typeof zSceneManifest>;
export type SceneSection = z.infer<typeof zSceneSection>;
export type SceneAssets = z.infer<typeof zSceneAssets>;
export type AssetRef = z.infer<typeof zAssetRef>;
export type AnimationConfig = z.infer<typeof zAnimationConfig>;
export type MobileBehavior = z.infer<typeof zMobileBehavior>;
export type SceneHero = z.infer<typeof zSceneManifest>['hero'];
export type DecorConfig = z.infer<typeof zDecorConfig>;
export type TypographyConfig = z.infer<typeof zTypographyConfig>;
